import React, { FC } from 'react';
import { View, Text, Animated, NativeSyntheticEvent, NativeScrollEvent, Image } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale, notchTop, notchBottom } from '@components/frame';
import { isPhone, isWeb, navigationRef } from '@helpers';

import { TAG_KEYS, TAG_NAMING, getThumbnailImageSize } from '@components/imageHandling/common';

import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import { ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types.d';

import Preview from '@components/imageHandling/components/thumbnails';
import { useGetImageUri } from '@v2/components/imageHandling/hooks/useGetImageUri';
import { HeaderDataItem, LinkBlockProps, PictureProps, PositionItem, ThumbnailScrollProps } from './types.d';
import TouchableElement from '@components/common/touchableElement';

let listPositing: Record<string, PositionItem> = {};

const Picture: FC<PictureProps> = ({ img, index, setShowActions, C, imageSize, imagesInLine, marginRightBase, setMode, setSelectedIndex, scale }) => {
  // const [loaded, setLoaded] = useState(false);

  let marginRight = (index + 1) % imagesInLine === 0 ? 0 : marginRightBase;

  const { getLowResUri } = useGetImageUri();
  const lowResUri = getLowResUri(img);

  const onImagePress = () => {
    setSelectedIndex(img.index);

    setTimeout(() => {
      if (!isWeb) {
        setShowActions(false);
      }
      setTimeout(() => {
        navigationRef.current.setParams({ imageHandlingMode: ImageHandlingDisplayMode.PICTURE });
        setMode(ImageHandlingDisplayMode.PICTURE);
      }, 50);
    }, 1);
  };

  return (
    <TouchableElement onPress={onImagePress}>
      {() => (
        <View
          style={{
            backgroundColor: C('Gray/Dark/5', !isWeb),
            width: imageSize,
            height: imageSize,
            marginRight: marginRight,
            marginTop: scale(2),
          }}
        >
          {img.isDocument ? (
            <Preview {...{ item: img, isThumbnail: true, withoutDownloadButton: true }} />
          ) : (
            <View
              style={{
                transform: [
                  {
                    rotate: `${img.customRotate[img.pageNumber] || 0}deg`,
                  },
                ],
              }}
            >
              <Image source={{ uri: lowResUri }} style={{ width: imageSize, height: imageSize }} resizeMode="cover" />
            </View>
          )}
          {/* {!loaded && (
            <Element
              {...{
                config: {
                  position: {
                    right: 4,
                    bottom: 6,
                  },
                },
              }}
            >
              <Svg icon="cloud" color="Background/Light/Field lighter" width={scale(18.5)} height={scale(12.5)} />
            </Element>
          )} */}
          {/* scale={scale} */}
          {/* </TouchableElement> */}
        </View>
      )}
    </TouchableElement>
  );
};

const LinkBlock: FC<LinkBlockProps> = (props) => {
  const {
    C,
    linkData: {
      link: { scopeId, scopeReferenceId: value, scopeReferenceName: name },
      images,
    },
    addHeader,
    fadeAnim,
    headers,
    groupedImages,
    scale,
    font,
    setMode,
    setSelectedIndex,
    setShowActions,
  } = props;

  const { marginRightBase, imagesInLine, imageSize } = getThumbnailImageSize();

  const key = TAG_KEYS[scopeId as keyof typeof TAG_KEYS];

  const header = headers[`${key}-${value}`];

  return (
    <View
      style={{ marginBottom: scale(9), marginTop: scale(9) }}
      onLayout={(event) => {
        const layout = event.nativeEvent.layout;
        if (layout.y <= 0) {
          return;
        }
        const data = { position: layout.y, height: layout.height, data: { key, value, name } };
        listPositing[`${key}-${value}`] = data;
        if (Object.keys(listPositing).length === Object.keys(groupedImages).length && data.position > 0) {
          addHeader(listPositing);
          listPositing = {};
        }
      }}
    >
      {header && (
        <Animated.View
          style={{
            paddingLeft: scale(18),
            marginBottom: scale(6),
            opacity: fadeAnim.interpolate({
              inputRange: [0, header.position - (notchTop + scale(56, false)), header.position - (notchTop + scale(55.5, false))],
              outputRange: [1, 1, 0],
              extrapolate: 'clamp',
            }),
          }}
        >
          <Text style={[{ color: C('Label/Dark/Primary', !isWeb) }, font.Headline]}>{i18n.t(TAG_NAMING[key])}</Text>
          <Text style={[{ color: C('Label/Dark/Tertiary', !isWeb) }, font['Subhead 2']]}>{name}</Text>
        </Animated.View>
      )}
      <View style={{ flexWrap: 'wrap', flexDirection: 'row' }}>
        {images.map((img, index) => (
          <Picture
            {...{
              img,
              index,
              scale,
              C,
              imagesInLine,
              imageSize,
              marginRightBase,
              setMode,
              setSelectedIndex,
              setShowActions,
            }}
            key={`${img.id}-gallery`}
          />
        ))}
      </View>
    </View>
  );
};

const ThumbnailScroll: FC<ThumbnailScrollProps> = (props) => {
  const {
    groupedImages,
    scrollRef,
    fadeAnim,
    setScrolled,
    groupedImagesKeys,
    headers,
    setHeaders,
    C,
    setMode,
    setSelectedIndex,
    setShowActions,
    scale = initialScale,
    font = initialFont,
  } = props;

  const addHeader = (data: HeaderDataItem) => {
    setHeaders(data);
  };

  const scrollListener = ({
    nativeEvent: {
      contentOffset: { y },
    },
  }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const isScrolled = y >= scale(5);
    if (isScrolled) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  return (
    <View style={{ height: '100%' }}>
      <Animated.ScrollView
        scrollEventThrottle={1}
        ref={scrollRef}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], {
          useNativeDriver: false,
          listener: scrollListener,
        })}
      >
        <View style={{ height: scale(48) + notchTop }} />
        {groupedImagesKeys.map((key) => (
          <LinkBlock
            {...{
              linkData: groupedImages[key],
              groupedImages,
              addHeader,
              fadeAnim,
              headers,
              C,
              scale,
              font,
              setMode,
              setSelectedIndex,
              setShowActions,
            }}
            key={key}
          />
        ))}
        {isPhone && <View style={{ height: scale(60) + notchBottom }} />}
      </Animated.ScrollView>
    </View>
  );
};

export default ThumbnailScroll;
